import { useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Vehicle, VehicleLocation } from "../../../../api/vehiclesApi";
import { useSignalR } from "../../../../contexts/SignalRContext";
import {
  createHistoryPolyline,
  createPauseMarker,
  createStartMarker,
  processVehicleHistory,
} from "../../utils/vehicleHistoryUtils";

const HISTORY_LOAD_TIMEOUT_MS = 20000;

interface UseHistoryHandlerProps {
  map: google.maps.Map | null;
  selectedVehicleId?: number;
  selectedVehicle?: Vehicle | null;
  token?: string | null;
  showRoute: boolean;
  onHistoryProcessed: (history: VehicleLocation[]) => void;
}

export const useHistoryHandler = ({
  map,
  selectedVehicleId,
  selectedVehicle,
  token,
  showRoute,
  onHistoryProcessed,
}: UseHistoryHandlerProps) => {
  const theme = useTheme();
  const {
    isConnected,
    getVehicleLocationHistory,
    setVehicleHistoryCallback,
    setVehicleUpdateCallback,
  } = useSignalR();
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const markerLibraryRef = useRef<
    typeof google.maps.marker.AdvancedMarkerElement | null
  >(null);
  const processedHistoryRef = useRef<string | null>(null);
  const historyDataRef = useRef<VehicleLocation[]>([]);
  const selectedVehicleRef = useRef<Vehicle | null>(null);
  const pendingRequestRef = useRef<number | null>(null);
  const previousVehicleIdRef = useRef<number | undefined>(selectedVehicleId);
  const [isHistoryReady, setIsHistoryReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const onHistoryProcessedRef = useRef(onHistoryProcessed);
  const showRouteRef = useRef(showRoute);
  const routeAnimationRef = useRef<{
    frameId: number;
    pointIndex: number;
    target: google.maps.LatLngLiteral;
  } | null>(null);

  const ROUTE_ANIMATION_DURATION = 10000;

  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const loadMarkerLibrary = useCallback(async () => {
    if (!markerLibraryRef.current) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        "marker"
      )) as google.maps.MarkerLibrary;
      markerLibraryRef.current = AdvancedMarkerElement;
    }
    return markerLibraryRef.current;
  }, []);

  const clearMapObjects = useCallback(() => {
    if (routeAnimationRef.current?.frameId) {
      cancelAnimationFrame(routeAnimationRef.current.frameId);
      routeAnimationRef.current = null;
    }

    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
  }, []);

  const resetHistoryState = useCallback(() => {
    clearMapObjects();
    processedHistoryRef.current = null;
    historyDataRef.current = [];
    pendingRequestRef.current = null;
    setIsHistoryReady(false);
    setIsLoading(false);
  }, [clearMapObjects]);

  useEffect(() => {
    selectedVehicleRef.current = selectedVehicle ?? null;
  }, [selectedVehicle]);

  useEffect(() => {
    onHistoryProcessedRef.current = onHistoryProcessed;
  }, [onHistoryProcessed]);

  useEffect(() => {
    showRouteRef.current = showRoute;
  }, [showRoute]);

  const handleHistory = useCallback(
    async (history: VehicleLocation[]) => {
      const selected = selectedVehicleRef.current;
      pendingRequestRef.current = null;

      if (!map || !selected) {
        setIsLoading(false);
        return;
      }

      const historyKey = JSON.stringify(history.map((h) => h.timestamp));
      if (processedHistoryRef.current === historyKey) {
        setIsLoading(false);
        return;
      }

      const { path, pauses, startPosition } = processVehicleHistory(history);

      clearMapObjects();

      if (showRouteRef.current) {
        await loadMarkerLibrary();
        if (markerLibraryRef.current) {
          if (startPosition) {
            const startMarker = createStartMarker(startPosition, map);
            markersRef.current.push(startMarker);
          }

          pauses.forEach((pause, index) => {
            const pauseMarker = createPauseMarker(
              pause,
              map,
              index,
              pauses.length,
              theme.palette.mode === "dark"
            );
            markersRef.current.push(pauseMarker);
          });

          if (path.length > 0) {
            polylineRef.current = createHistoryPolyline(
              path,
              map,
              theme.palette.mode === "dark"
            );

            const bounds = new google.maps.LatLngBounds();
            path.forEach((pos) => bounds.extend(pos));
            pauses.forEach((stop) => bounds.extend(stop.position));
            if (startPosition) {
              bounds.extend(startPosition);
            }

            if (selected.lastLatitude && selected.lastLongitude) {
              bounds.extend({
                lat: selected.lastLatitude,
                lng: selected.lastLongitude,
              });
            }

            map.fitBounds(bounds, 100);
          }
        }
      }

      processedHistoryRef.current = historyKey;
      historyDataRef.current = [...history];
      setIsHistoryReady(true);
      setIsLoading(false);
      onHistoryProcessedRef.current?.(history);
    },
    [map, theme.palette.mode, clearMapObjects, loadMarkerLibrary]
  );

  const handleLiveUpdate = useCallback(
    (update: VehicleLocation) => {
      const selected = selectedVehicleRef.current;
      if (
        !map ||
        !showRouteRef.current ||
        !selected ||
        update.vehicleId !== selectedVehicleId ||
        !polylineRef.current ||
        !isHistoryReady
      ) {
        return;
      }

      historyDataRef.current = [...historyDataRef.current, update];
      const path = polylineRef.current.getPath();
      const lastIndex = path.getLength() - 1;
      const lastPoint =
        lastIndex >= 0
          ? path.getAt(lastIndex)
          : new google.maps.LatLng(update.latitude, update.longitude);

      const startPoint = {
        lat: lastPoint.lat(),
        lng: lastPoint.lng(),
      };
      const endPoint = {
        lat: update.latitude,
        lng: update.longitude,
      };

      const animateSegment = () => {
        if (!polylineRef.current) {
          routeAnimationRef.current = null;
          return;
        }

        const pathRef = polylineRef.current.getPath();
        const newPointIndex = pathRef.getLength();
        pathRef.push(new google.maps.LatLng(startPoint.lat, startPoint.lng));

        const startTime = performance.now();

        const step = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / ROUTE_ANIMATION_DURATION, 1);
          const eased = easeInOutCubic(progress);

          const lat = startPoint.lat + (endPoint.lat - startPoint.lat) * eased;
          const lng = startPoint.lng + (endPoint.lng - startPoint.lng) * eased;

          pathRef.setAt(newPointIndex, new google.maps.LatLng(lat, lng));

          if (progress < 1) {
            const frameId = requestAnimationFrame(step);
            routeAnimationRef.current = {
              frameId,
              pointIndex: newPointIndex,
              target: endPoint,
            };
          } else {
            pathRef.setAt(
              newPointIndex,
              new google.maps.LatLng(endPoint.lat, endPoint.lng)
            );
            routeAnimationRef.current = null;
          }
        };

        const frameId = requestAnimationFrame(step);
        routeAnimationRef.current = {
          frameId,
          pointIndex: newPointIndex,
          target: endPoint,
        };
      };

      if (routeAnimationRef.current?.frameId && polylineRef.current) {
        cancelAnimationFrame(routeAnimationRef.current.frameId);
        const { pointIndex, target } = routeAnimationRef.current;
        const pathRef = polylineRef.current.getPath();
        if (pointIndex < pathRef.getLength()) {
          pathRef.setAt(
            pointIndex,
            new google.maps.LatLng(target.lat, target.lng)
          );
        }
        routeAnimationRef.current = null;
      }

      animateSegment();
      onHistoryProcessedRef.current?.(historyDataRef.current);
    },
    [isHistoryReady, map, selectedVehicleId]
  );

  useEffect(() => {
    if (previousVehicleIdRef.current === selectedVehicleId) {
      return;
    }

    previousVehicleIdRef.current = selectedVehicleId;
    resetHistoryState();
  }, [resetHistoryState, selectedVehicleId]);

  useEffect(() => {
    if (!selectedVehicleId || !map || !token || !isConnected) {
      setVehicleHistoryCallback(undefined);
      return;
    }

    setVehicleHistoryCallback(handleHistory);
    return () => setVehicleHistoryCallback(undefined);
  }, [
    handleHistory,
    isConnected,
    map,
    selectedVehicleId,
    setVehicleHistoryCallback,
    token,
  ]);

  useEffect(() => {
    if (!showRoute || !selectedVehicleId || !map || !token) {
      setIsLoading(false);
      return;
    }

    if (!selectedVehicleRef.current) {
      return;
    }

    if (historyDataRef.current.length > 0) {
      setIsLoading(false);
      return;
    }

    if (!isConnected) {
      setIsLoading(true);
      return;
    }

    if (pendingRequestRef.current === selectedVehicleId) {
      setIsLoading(true);
      return;
    }

    pendingRequestRef.current = selectedVehicleId;
    setIsLoading(true);

    void getVehicleLocationHistory(selectedVehicleId, 5000, 3).catch(
      (error) => {
        console.error("❌ Error requesting vehicle location history:", error);
        if (pendingRequestRef.current === selectedVehicleId) {
          pendingRequestRef.current = null;
        }
        setIsLoading(false);
      }
    );
  }, [
    getVehicleLocationHistory,
    isConnected,
    map,
    selectedVehicleId,
    showRoute,
    token,
  ]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const timeout = setTimeout(() => {
      pendingRequestRef.current = null;
      setIsLoading(false);
    }, HISTORY_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  useEffect(() => {
    if (!showRoute) {
      clearMapObjects();
      return;
    }

    if (historyDataRef.current.length > 0 && map) {
      processedHistoryRef.current = null;
      handleHistory(historyDataRef.current);
    }
  }, [showRoute, map, clearMapObjects, handleHistory]);

  useEffect(() => {
    if (
      !isHistoryReady ||
      !selectedVehicleId ||
      !showRoute ||
      !selectedVehicleRef.current
    ) {
      setVehicleUpdateCallback(undefined);
      return;
    }

    setVehicleUpdateCallback(handleLiveUpdate);
    return () => setVehicleUpdateCallback(undefined);
  }, [
    handleLiveUpdate,
    isHistoryReady,
    selectedVehicleId,
    setVehicleUpdateCallback,
    showRoute,
  ]);

  const fitRouteBounds = useCallback(() => {
    const selected = selectedVehicleRef.current;
    if (!map || !selected) return;

    // Get the current vehicle history data
    const historyKey = processedHistoryRef.current;
    if (!historyKey) return;

    // We need to get the current history data to fit bounds
    // This will be called when the route is already displayed
    const bounds = new google.maps.LatLngBounds();

    // Add current vehicle position
    if (selected.lastLatitude && selected.lastLongitude) {
      bounds.extend({
        lat: selected.lastLatitude,
        lng: selected.lastLongitude,
      });
    }

    // Add all existing markers (start, pauses, route points)
    markersRef.current.forEach((marker) => {
      if (marker.position) {
        bounds.extend(marker.position);
      }
    });

    // Add polyline path if it exists
    if (polylineRef.current && polylineRef.current.getPath()) {
      const path = polylineRef.current.getPath();
      for (let i = 0; i < path.getLength(); i++) {
        bounds.extend(path.getAt(i));
      }
    }

    // Fit bounds with padding
    map.fitBounds(bounds, 100);
  }, [map]);

  return { clearMapObjects, fitRouteBounds, isLoading };
};
